// src/components/StripePaymentForm.jsx
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import {
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

function formatCurrency(cents, currency = 'usd') {
  const numericCents = Number(cents);
  if (!Number.isFinite(numericCents)) return null;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'usd',
  }).format(numericCents / 100);
}

export default function StripePaymentForm({
  clientSecret,
  amountCents,
  amount,
  currency = 'usd',
  onComplete,
  processing,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [error, setError] = useState(null);
  const [localProcessing, setLocalProcessing] = useState(false);

  // Historical callers passed amount_cents through the `amount` prop.
  // Keep interpreting `amount` as cents so checkout buttons do not show $999.00 for 999 cents.
  const displayAmount = formatCurrency(amountCents ?? amount, currency);
  const isLoading = processing || localProcessing;

  const handleSubmit = async () => {
    if (!stripe || !elements || isLoading) return;

    setError(null);
    setLocalProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (result.error) {
        setError(result.error.message);
        return;
      }

      if (!result.paymentIntent?.id) {
        throw new Error('Stripe did not return a payment intent.');
      }

      if (result.paymentIntent.status !== 'succeeded') {
        throw new Error(
          `Payment is not complete yet (${result.paymentIntent.status || 'unknown'}).`
        );
      }

      await onComplete?.(result.paymentIntent.id);
    } catch (err) {
      setError(err?.message || 'Payment failed. Try again.');
    } finally {
      setLocalProcessing(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" sx={{ mb: 0.75, display: 'block', color: 'text.secondary' }}>
          Card details
        </Typography>

        <Box sx={{ p: 1.5, border: '1px solid #ccc', borderRadius: 1 }}>
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        disabled={!stripe || isLoading}
        size="large"
        startIcon={
          isLoading ? (
            <CircularProgress size={18} sx={{ color: 'rgba(255,255,255,0.6)' }} />
          ) : (
            <LockOutlinedIcon sx={{ fontSize: 18 }} />
          )
        }
        sx={{ py: 1.7, fontSize: 16 }}
      >
        {isLoading ? 'Processing...' : displayAmount ? `Pay ${displayAmount}` : 'Pay'}
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 1.5 }}>
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>Powered by</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#6772E5', letterSpacing: '-0.3px' }}>
          stripe
        </Typography>
      </Box>
    </Box>
  );
}
